import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try to get token from either 'adminToken' (admin pages) or 'token' (public auth)
    const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Adding token to request:', token.substring(0, 20) + '...');
    } else {
      console.log('No token found in localStorage');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log('API Error:', error.response?.status, error.response?.data);
    if (error.response?.status === 401) {
      console.log('Unauthorized - removing token and redirecting to login');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Company Information API
export const companyAPI = {
  getInfo: () => api.get('/company'),
  updateInfo: (data) => api.put('/company', data),
  getStats: () => api.get('/company/stats'),
};

// Services API
export const servicesAPI = {
  getAll: (params = {}) => api.get('/services', { params }),
  getById: (id) => api.get(`/services/${id}`),
  getBySlug: (slug) => api.get(`/services/slug/${slug}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Products API
export const productsAPI = {
  getAll: (params = {}) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Values API
export const valuesAPI = {
  getAll: () => api.get('/values'),
  create: (data) => api.post('/values', data),
  update: (id, data) => api.put(`/values/${id}`, data),
  delete: (id) => api.delete(`/values/${id}`),
};

// Process Steps API
export const processAPI = {
  getAll: () => api.get('/process'),
  create: (data) => api.post('/process', data),
  update: (id, data) => api.put(`/process/${id}`, data),
  delete: (id) => api.delete(`/process/${id}`),
};

// Portfolio API
export const portfolioAPI = {
  getAll: (params = {}) => api.get('/portfolio', { params }),
  create: (data) => api.post('/portfolio', data),
  update: (id, data) => api.put(`/portfolio/${id}`, data),
  delete: (id) => api.delete(`/portfolio/${id}`),
};

// Team API
export const teamAPI = {
  getAll: (params = {}) => api.get('/team', { params }),
  create: (data) => api.post('/team', data),
  update: (id, data) => api.put(`/team/${id}`, data),
  delete: (id) => api.delete(`/team/${id}`),
};

// Testimonials API
export const testimonialsAPI = {
  getAll: (params = {}) => api.get('/testimonials', { params }),
  create: (data) => api.post('/testimonials', data),
  update: (id, data) => api.put(`/testimonials/${id}`, data),
  delete: (id) => api.delete(`/testimonials/${id}`),
};

// Technologies API
export const technologiesAPI = {
  getAll: (params = {}) => api.get('/technologies', { params }),
  create: (data) => api.post('/technologies', data),
  update: (id, data) => api.put(`/technologies/${id}`, data),
  delete: (id) => api.delete(`/technologies/${id}`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  create: (data) => api.post('/contact', data), // Alias for submit
  getMessages: (params = {}) => api.get('/contact/messages', { params }),
  getMessage: (id) => api.get(`/contact/messages/${id}`),
  updateMessage: (id, data) => api.put(`/contact/messages/${id}`, data),
  deleteMessage: (id) => api.delete(`/contact/messages/${id}`),
  getStats: () => api.get('/contact/messages/stats'),
};

// Authentication API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/password', data),
};

// File Upload API
export const uploadAPI = {
  single: (file, type = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    
    return api.post('/upload/single', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  multiple: (files, type = 'general') => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('type', type);
    
    return api.post('/upload/multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  delete: (type, filename) => api.delete(`/upload/${type}/${filename}`),
};

// Blog API
export const blogAPI = {
  getAll: (params = {}) => api.get('/blog', { params }),
  getBySlug: (slug) => api.get(`/blog/slug/${slug}`),
  getFeatured: (limit = 3) => api.get('/blog/featured', { params: { limit } }),
  getCategories: () => api.get('/blog/categories'),
  create: (data) => api.post('/blog', data),
  update: (id, data) => api.put(`/blog/${id}`, data),
  delete: (id) => api.delete(`/blog/${id}`),
  autoSave: (id, data) => api.post(`/blog/${id}/auto-save`, data),
  bulkUpdate: (data) => api.post('/blog/bulk-update', data),
  publishScheduled: () => api.post('/blog/publish-scheduled'),
};

// Jobs API
export const jobsAPI = {
  getAll: (params = {}) => api.get('/jobs', { params }),
  getBySlug: (slug) => api.get(`/jobs/slug/${slug}`),
  getDepartments: () => api.get('/jobs/departments'),
  create: (data) => api.post('/jobs', data),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  apply: (jobId, data) => api.post(`/jobs/${jobId}/apply`, data),
};

// Job Applications API (Admin)
export const jobApplicationsAPI = {
  getAll: () => api.get('/admin/job-applications'),
  getById: (id) => api.get(`/admin/job-applications/${id}`),
  updateStatus: (id, status) => api.put(`/admin/job-applications/${id}/status`, { status }),
  updateNotes: (id, notes) => api.put(`/admin/job-applications/${id}/notes`, { notes }),
  updateScores: (id, scores) => api.put(`/admin/job-applications/${id}/scores`, scores),
  delete: (id) => api.delete(`/admin/job-applications/${id}`),
};

// Chat API
export const chatAPI = {
  sendMessage: (data) => api.post('/chat/message', data),
  getHistory: (sessionId, limit = 20) => api.get(`/chat/history/${sessionId}`, { params: { limit } }),
  rateConversation: (data) => api.post('/chat/rate', data),
  resolveConversation: (data) => api.post('/chat/resolve', data),
  // Admin routes
  getConversations: (params = {}) => api.get('/chat/admin/conversations', { params }),
  getIntents: () => api.get('/chat/admin/intents'),
  createIntent: (data) => api.post('/chat/admin/intents', data),
  updateIntent: (id, data) => api.put(`/chat/admin/intents/${id}`, data),
  deleteIntent: (id) => api.delete(`/chat/admin/intents/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getActivity: (limit = 10) => api.get('/dashboard/activity', { params: { limit } }),
  getChartData: (days = 30) => api.get('/dashboard/charts', { params: { days } }),
};

// Analytics API
export const analyticsAPI = {
  getOverview: (params) => api.get('/analytics/overview', { params }),
  getTraffic: (params) => api.get('/analytics/traffic', { params }),
  getConversions: (params) => api.get('/analytics/conversions', { params }),
  getContent: (params) => api.get('/analytics/content', { params }),
  getChatbot: (params) => api.get('/analytics/chatbot', { params }),
  exportReport: (data) => api.post('/analytics/export', data, { responseType: 'blob' }),
};

export default api;